import { DateTime, Interval } from 'luxon';
import {
  InventoryReturnRequest,
  OffRentRequest,
  Order,
  OrderProduct,
} from '../../../../../clients/quipli-service/order/order';
import { DateTimePicker } from '../../../../../components/date-picker/date-time-picker';
import { Checkbox } from '../../../../../components/checkbox/checkbox';
import { Typography } from '../../../../../components/typography/typography';
import { ValidationError } from '../../../../../components/validation-error/validation-error';
import { Toggle } from '../../../../../components/form/toggle';
import { PaymentReason, PaymentStatus } from '../../../../../clients/quipli-service/payment/payment';
import { Dictionary } from 'lodash';
import { Configuration } from '../../../../../clients/quipli-service/configuration/configuration';
import { Location } from '../../../../../clients/quipli-service/location/location';

export const OffRentHeader = ({
  offRentRequest,
  setOffRentRequest,
  order,
  onOffRentUpdateDateConfiguration,
  onOffRentUpdateDateCheckbox,
  setOnOffRentUpdateDateCheckbox,
  showInventoryReturn,
  inventoryReturnRequest,
  locations,
  configs,
  setWaiveLateCharge,
  waiveLateCharge,
  setInventoryReturnRequest,
}: {
  offRentRequest: OffRentRequest;
  setOffRentRequest: React.Dispatch<React.SetStateAction<OffRentRequest | undefined>>;
  order: Order;
  onOffRentUpdateDateConfiguration: boolean;
  onOffRentUpdateDateCheckbox: boolean;
  setOnOffRentUpdateDateCheckbox: React.Dispatch<React.SetStateAction<boolean>>;
  showInventoryReturn: boolean | undefined;
  inventoryReturnRequest: InventoryReturnRequest | undefined;
  locations: Location[];
  configs: Dictionary<Configuration>;
  setWaiveLateCharge: React.Dispatch<React.SetStateAction<boolean>>;
  waiveLateCharge: boolean;
  setInventoryReturnRequest: React.Dispatch<React.SetStateAction<InventoryReturnRequest | undefined>>;
}) => {
  return (
    <>
      <div className="gap-2 flex flex-column">
        <h6 className="m-0">Off Rent At</h6>
        <DateTimePicker
          value={offRentRequest.timestamp}
          onChange={(d) =>
            d &&
            setOffRentRequest((r) => {
              return !r ? undefined : { ...r, timestamp: d };
            })
          }
          timezone={offRentRequest.timestamp?.zone.name}
          disabledIntervals={[
            Interval.fromDateTimes(
              DateTime.fromMillis(0),
              order.products
                .filter((op) => offRentRequest.offRentProducts.map((orp) => orp.productId).includes(op.id))
                .reduce((prev, cur) => {
                  return !prev && cur.actualRentalStartDate
                    ? cur
                    : !prev && !cur.actualRentalStartDate
                    ? undefined
                    : !cur.actualRentalStartDate
                    ? prev
                    : cur.actualRentalStartDate!.diff(prev!.actualRentalStartDate!).as('millisecond') < 0
                    ? cur
                    : prev;
                }, undefined as OrderProduct | undefined)?.actualRentalStartDate ?? DateTime.fromMillis(0),
            ),
          ]}
        />
        {!order.isCycleBilled && onOffRentUpdateDateConfiguration && (
          <Checkbox
            label="Update product end with off-rent date/time"
            isChecked={onOffRentUpdateDateCheckbox}
            onChange={setOnOffRentUpdateDateCheckbox}
            data-cy="update-product-end-checkbox"
          />
        )}
        {showInventoryReturn && inventoryReturnRequest?.orderProductIds?.length && locations ? (
          <Typography className="my-2" type="text-sm" weight="medium">
            Returning to: {locations.find((l) => l.id === inventoryReturnRequest.returnToLocation)?.name}
          </Typography>
        ) : (
          <></>
        )}
        {configs['CONF_SEIS_INTEGRATION_ENABLED']?.value === '1' &&
          order.damageWaiver !== undefined &&
          order.damageWaiver > 0 &&
          offRentRequest.timestamp.diffNow().as('hours') < 0 && (
            // TODO: check if there are even any seis certs for order
            <ValidationError className="warning">
              <ul>
                <li data-cy="seis-warning">
                  If applicable, rental damage waiver insurance cannot be updated automatically if the off rent date is
                  after the rental end date because the RDW has already expired. You must contact SEIS directly to
                  correct this.
                </li>
              </ul>
            </ValidationError>
          )}
        {!order.isCycleBilled && (
          <div className="flex gap-2 text-[0.9rem] font-bold items-center py-2">
            <Toggle
              onClick={() => {
                setWaiveLateCharge((prev) => !prev);
              }}
              checked={waiveLateCharge}
              data-cy="waive-late-charge-toggle"
            />
            Waive Late Charge
          </div>
        )}
      </div>

      {showInventoryReturn &&
      inventoryReturnRequest?.orderProductIds?.length &&
      order.payments.find(
        (op) => op.reason === PaymentReason.SecurityDeposit && op.status === PaymentStatus.pending,
      ) ? (
        <div className="flex gap-2 text-[0.9rem] font-bold items-center py-2">
          <Toggle
            onClick={() => {
              setInventoryReturnRequest(
                (prev) => prev && { ...prev, chargeSecurityDeposit: !prev.chargeSecurityDeposit },
              );
            }}
            checked={inventoryReturnRequest.orderProductIds.length > 0 && inventoryReturnRequest.chargeSecurityDeposit}
            disabled={inventoryReturnRequest.orderProductIds.length === 0}
            data-cy="charge-security-deposit-toggle"
          />
          Charge Security Deposit
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
